import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { PlusOutlined } from "@ant-design/icons";
import {
  Table,
  Typography,
  Spin,
  Alert,
  Button,
  Modal,
  Input,
  Select,
  Row,
  Col,
  InputNumber,
} from 'antd';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { Title, Paragraph } = Typography;
const { TextArea, Search } = Input;
const { Option } = Select;

const safe = (v) => (v === null || v === undefined || v === '' ? '-' : v);

function PaymentLinks() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  // Auth verification
  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );
        if (!res?.data?.success) {
          toast.error(res?.data?.message || 'Please login again.');
          localStorage.clear();
          navigate('/login');
        }
      } catch (err) {
        toast.error('Session verification failed. Please login again.');
        localStorage.clear();
        navigate('/login');
      }
    };
    verifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, token, email, role]);

  // state
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters / search
  const [courseFilter, setCourseFilter] = useState(null);
  const [searchText, setSearchText] = useState('');

  // add modal
  const [addVisible, setAddVisible] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [form, setForm] = useState({
    CourseName: '',
    Installment: '',
    Link: '',
    Amount: '',
    Description: '',
  });

  // edit modal
  const [editVisible, setEditVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  // Course options (uppercase as you store CourseName uppercase)
  const courseOptions = [
    'JUNIOR PENETRATION TESTER',
    'SENIOR PENETRATION TESTER',
  ];

  const installmentOptions = ['YES', 'NO'];

  // Action modal (for Delete)
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch payment links
  const fetchPaymentLinks = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${process.env.REACT_APP_API_URL}/paymentlinks/all-payment-links`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 20000,
      });

      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      const normalized = (Array.isArray(data) ? data : []).map((r, i) => ({
        key: r.id ?? `${i}`,
        ...r,
      }));
      setLinks(normalized);
    } catch (err) {
      console.error('Failed to fetch payment links', err);
      setError('Failed to load payment links');
      toast.error('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.key]);

  // Open action modal (called by Actions button)
  const openActionModal = (record) => {
    setSelectedLink(record);
    setActionModalVisible(true);
  };

  // Delete payment link (sends id in req body)
  const handleDeletePaymentLink = async () => {
    if (!selectedLink) return;
    setDeleteLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/paymentlinks/delete-payment-link`;
      const res = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: { id: selectedLink.id ?? selectedLink.key },
        timeout: 20000,
      });

      if (res?.data?.success || res?.status === 200) {
        toast.success(res?.data?.message || 'Payment link deleted successfully');
        setActionModalVisible(false);
        setSelectedLink(null);
        await fetchPaymentLinks();
      } else {
        toast.error(res?.data?.message || 'Failed to delete payment link');
      }
    } catch (err) {
      console.error('Delete payment link error', err);
      toast.error(err?.response?.data?.message || 'Error deleting payment link');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Open edit modal when clicking CourseName
  const openEditModal = (record) => {
    setEditRecord({
      id: record.id ?? record.key,
      CourseName: record.CourseName ?? '',
      Installment: record.Installment ?? '',
      Link: record.Link ?? '',
      Amount: record.Amount ?? '',
      Description: record.Description ?? '',
    });
    setEditVisible(true);
  };

  const handleEditFieldChange = (field, value) => {
    setEditRecord((prev) => ({ ...prev, [field]: value }));
  };

  // Update handler (partial updates allowed)
  const handleUpdatePaymentLink = async () => {
    if (!editRecord || !editRecord.id) {
      toast.error('Invalid record');
      return;
    }

    // Build payload with only provided fields (non-empty or explicit zero)
    const payload = { id: editRecord.id };
    const maybeAdd = (k, v) => {
      if (v !== undefined && v !== null) {
        if (typeof v === 'string') {
          if (v.trim() !== '') payload[k] = v.trim();
        } else {
          payload[k] = v;
        }
      }
    };

    maybeAdd('CourseName', editRecord.CourseName ? String(editRecord.CourseName).toUpperCase() : undefined);
    maybeAdd('Installment', editRecord.Installment);
    maybeAdd('Link', editRecord.Link);
    if (editRecord.Amount !== undefined && editRecord.Amount !== null && editRecord.Amount !== '') {
      payload.Amount = Number(editRecord.Amount) || 0;
    }
    maybeAdd('Description', editRecord.Description);

    const keys = Object.keys(payload).filter((k) => k !== 'id');
    if (keys.length === 0) {
      toast.error('No fields provided for update.');
      return;
    }

    setEditLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/paymentlinks/update-payment-link`;
      const res = await axios.put(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 20000,
      });

      if (res?.data?.success || res?.status === 200) {
        toast.success(res?.data?.message || 'Payment link updated successfully');
        setEditVisible(false);
        setEditRecord(null);
        await fetchPaymentLinks();
      } else {
        toast.error(res?.data?.message || 'Failed to update payment link');
      }
    } catch (err) {
      console.error('Update payment link error', err);
      toast.error(err?.response?.data?.message || 'Error updating payment link');
    } finally {
      setEditLoading(false);
    }
  };

  // table columns (CourseName clickable)
  const columns = [
    {
      title: <span className="font-bold text-[17px] text-white whitespace-nowrap">Course Name</span>,
      dataIndex: 'CourseName',
      key: 'CourseName',
      width: 260,
      render: (t, record) => (
        <button
          onClick={() => openEditModal(record)}
          className="p-0 font-bold text-blue-600 underline bg-transparent border-0 cursor-pointer"
          type="button"
        >
          {safe(t)}
        </button>
      ),
    },
    {
      title: <span className="font-bold text-[17px] text-white whitespace-nowrap">Installment</span>,
      dataIndex: 'Installment',
      key: 'Installment',
      width: 160,
      render: (t) => <span className='font-bold ml-9'>{safe(t)}</span>,
    },
    {
      title: <span className="font-bold text-[17px] text-white whitespace-nowrap">Link</span>,
      dataIndex: 'Link',
      key: 'Link',
      width: 380,
      render: (t) =>
        t ? (
          <a className="ml-4 truncate inline-block max-w-[340px] whitespace-nowrap" href={t} target="_blank" rel="noreferrer">
            {t}
          </a>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: <span className="font-bold text-[17px] text-white whitespace-nowrap">Amount</span>,
      dataIndex: 'Amount',
      key: 'Amount',
      width: 160,
      render: (t) => <span className="ml-10 font-bold text-green-600 whitespace-nowrap">{t ?? '-'}</span>,
    },
    {
      title: <span className="font-bold text-[17px] text-white whitespace-nowrap">Description</span>,
      dataIndex: 'Description',
      key: 'Description',
      width: 420,
      render: (t) => <span className="truncate inline-block max-w-[380px] whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: (
        <span className="font-bold text-[17px] text-white whitespace-nowrap">
          Action
        </span>
      ),
      key: 'action',
      fixed: 'right',
      width: 200,
      align: 'center',
      render: (_, record) => {
        return (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => openActionModal(record)}
              className="px-4 py-2 font-semibold text-white transition-colors duration-200 rounded-md shadow whitespace-nowrap bg-gradient-to-r from-blue-600 to-blue-500 hover:from-red-700 hover:to-rose-600"
              type="button"
            >
              Actions
            </button>
          </div>
        );
      },
    },
  ];

  // filtering & search
  const filtered = useMemo(() => {
    return links.filter((r) => {
      if (courseFilter) {
        if (!r.CourseName) return false;
        if (String(r.CourseName).toUpperCase() !== String(courseFilter).toUpperCase()) return false;
      }
      if (searchText) {
        const s = searchText.toLowerCase();
        const hay = `${r.CourseName || ''} ${r.Installment || ''} ${r.Link || ''} ${r.Description || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [links, courseFilter, searchText]);

  // Add modal helpers
  const openAddModal = () => {
    setForm({ CourseName: '', Installment: '', Link: '', Amount: '', Description: '' });
    setAddVisible(true);
  };

  const validateAdd = () => {
    if (!form.CourseName || !form.CourseName.trim()) {
      toast.error('CourseName is required');
      return false;
    }
    if (!form.Link || !form.Link.trim()) {
      toast.error('Link is required');
      return false;
    }
    if (!form.Amount && form.Amount !== 0) {
      toast.error('Amount is required');
      return false;
    }
    if (!form.Description || !form.Description.trim()) {
      toast.error('Description is required');
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateAdd()) return;
    setAddLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/paymentlinks/add-payment-link`;
      const payload = {
        CourseName: String(form.CourseName).toUpperCase(),
        Installment: form.Installment || '',
        Link: form.Link,
        Amount: Number(form.Amount) || 0,
        Description: form.Description,
      };

      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 20000,
      });

      if (res?.data?.success || res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || 'Payment link added');
        setAddVisible(false);
        await fetchPaymentLinks();
      } else {
        toast.error(res?.data?.message || 'Failed to add payment link');
      }
    } catch (err) {
      console.error('Add payment link error', err);
      toast.error(err?.response?.data?.message || 'Network/server error');
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div className="w-full py-4 mx-auto bg-white shadow-sm px-7 sm:px-6 lg:px-8 rounded-xl">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      <Title level={3} className="!text-3xl !font-bold text-gray-800 tracking-tight mb-3 border-b border-gray-200 pb-2">Payment Links</Title>
      <Paragraph className="mb-6 text-base text-gray-700">
        Manage payment links for courses. Add new payment links, filter by course type, and search existing links. Click the course name to edit a payment link (single or multiple field update).
      </Paragraph>

     <Paragraph className="mb-6 text-base font-bold text-red-700">
  Manage payment links for our training programs — 
  <strong> Junior Penetration Tester</strong> (beginner) and <strong> Senior Penetration Tester</strong> (advanced). 
  If <strong>Installment</strong> is <em>NO</em>, it’s a <strong>full payment</strong> link.
  
</Paragraph>


      {error && <Alert type="error" message="Error" description={error} showIcon closable className="mb-6" />}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="font-bold text-gray-700 text-md">Course Name</label>
          <Select
            allowClear
            placeholder={<span style={{ color: '#4b5563' }}>Select Course</span>}
            value={courseFilter}
            onChange={(v) => setCourseFilter(v || null)}
             style={{ minWidth: 160, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
            options={courseOptions.map((c) => ({ label: c, value: c }))}
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="sr-only">Search</label>
          <Search
            placeholder="Search by link, description, installment..."
            allowClear
            onSearch={(v) => setSearchText(v)}
            onChange={(e) => setSearchText(e.target.value)}
             style={{ minWidth: 260, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
            value={searchText}
            enterButton={false}
          />
        </div>

        <div className="ml-auto">
          <Button
            type="primary"
            onClick={openAddModal}
            icon={<PlusOutlined className="text-lg font-extrabold" />}
            className=" !bg-blue-700 !hover:bg-blue-900 !text-white !font-bold text-lg !px-6 !py-7 !rounded-md !shadow-lg"
          >
            Add Payment Link
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey={(r) => r.key}
          loading={loading}
          pagination={{ pageSize: 10, total: filtered.length }}
          scroll={{ x: 1400 }}
          className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table-payment"
        />
      </div>

      {/* Add Modal */}
      <Modal
        title={<span className="mb-6 text-lg font-bold">ADD PAYMENT LINK</span>}
        open={addVisible}
        onCancel={() => setAddVisible(false)}
        footer={null}
        width={780}
        centered
      >
        <div className="space-y-6">
          <Row gutter={12}>
            <Col span={12}>
              <label className="block mb-1 text-base font-bold">Course Name</label>
              <Select
                showSearch
                placeholder="Select or type course name"
                value={form.CourseName || undefined}
                onChange={(v) => setForm((p) => ({ ...p, CourseName: v }))}
                style={{ width: '100%' }}
                options={courseOptions.map((c) => ({ label: c, value: c }))}
              />
            </Col>
            <Col span={12}>
              <label className="block mb-1 text-base font-bold">Installment</label>
              <Select
                showSearch
                placeholder="select Installment"
                value={form.Installment || undefined}
                onChange={(v) => setForm((p) => ({ ...p, Installment: v }))}
                style={{ width: '100%' }}
                options={installmentOptions.map((c) => ({ label: c, value: c }))}
              />
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={16}>
              <label className="block mb-1 text-base font-bold">Link</label>
              <TextArea
                value={form.Link}
                onChange={(e) => setForm((p) => ({ ...p, Link: e.target.value }))}
                rows={2}
                placeholder="https://..."
              />
            </Col>
            <Col span={8}>
              <label className="block mb-1 text-base font-bold">Amount</label>
              <InputNumber
                value={form.Amount}
                onChange={(v) => setForm((p) => ({ ...p, Amount: v }))}
                style={{ width: '100%' }}
                placeholder="Amount"
              />
            </Col>
          </Row>

          <div>
            <label className="block mb-1 text-base font-bold">Description</label>
            <TextArea
              value={form.Description}
              onChange={(e) => setForm((p) => ({ ...p, Description: e.target.value }))}
              rows={3}
              placeholder="Description for this payment link"
            />
          </div>

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button onClick={() => setAddVisible(false)} disabled={addLoading}>Cancel</Button>
            <Button type="primary" onClick={handleAdd} disabled={addLoading}>
              {addLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" /> Adding...</span> : 'Add Payment Link'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={<span className="text-lg font-bold">EDIT PAYMENT LINK</span>}
        open={editVisible}
        onCancel={() => { setEditVisible(false); setEditRecord(null); }}
        footer={null}
        width={780}
        centered
      >
        {editRecord ? (
          <div className="space-y-6">
            <Row gutter={12}>
              <Col span={12}>
                <label className="block mb-1 text-base font-bold">Course Name</label>
                <Select
                  showSearch
                  placeholder="Select or type course name"
                  value={editRecord.CourseName || undefined}
                  onChange={(v) => handleEditFieldChange('CourseName', v)}
                  style={{ width: '100%' }}
                  options={courseOptions.map((c) => ({ label: c, value: c }))}
                />
              </Col>
              <Col span={12}>
                <label className="block mb-1 text-base font-bold">Installment</label>
                <Select
                  showSearch
                  placeholder="select Installment"
                  value={editRecord.Installment || undefined}
                  onChange={(v) => handleEditFieldChange('Installment', v)}
                  style={{ width: '100%' }}
                  options={installmentOptions.map((c) => ({ label: c, value: c }))}
                />
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={16}>
                <label className="block mb-1 text-base font-bold">Link</label>
                <TextArea
                  value={editRecord.Link}
                  onChange={(e) => handleEditFieldChange('Link', e.target.value)}
                  rows={2}
                  placeholder="https://..."
                />
              </Col>
              <Col span={8}>
                <label className="block mb-1 text-base font-bold">Amount</label>
                <InputNumber
                  value={editRecord.Amount}
                  onChange={(v) => handleEditFieldChange('Amount', v)}
                  style={{ width: '100%' }}
                  placeholder="Amount"
                />
              </Col>
            </Row>

            <div>
              <label className="block mb-1 text-base font-bold">Description</label>
              <TextArea
                value={editRecord.Description}
                onChange={(e) => handleEditFieldChange('Description', e.target.value)}
                rows={3}
                placeholder="Description for this payment link"
              />
            </div>

            <div className="flex items-center justify-end gap-3 mt-2">
              <Button onClick={() => { setEditVisible(false); setEditRecord(null); }} disabled={editLoading}>Cancel</Button>
              <Button type="primary" onClick={handleUpdatePaymentLink} disabled={editLoading}>
                {editLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" /> Updating...</span> : 'Update Payment Link'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center"><Spin /></div>
        )}
      </Modal>

      {/* Actions Modal (Delete) */}
      <Modal
        title={<span className="text-lg font-bold">DELETE PAYMENT LINK</span>}
        open={actionModalVisible}
        onCancel={() => { setActionModalVisible(false); setSelectedLink(null); }}
        footer={null}
        width={640}
        centered
      >
        <div className="p-3 space-y-6">
          <div>
            <p><strong>Course Name:</strong> <span className='font-bold text-red-600'>{safe(selectedLink?.CourseName)}</span></p>
            <p><strong>Installment:</strong> {safe(selectedLink?.Installment)}</p>
            <p><strong>Amount:</strong> {selectedLink?.Amount ?? '-'}</p>
            <p className="truncate"><strong>Link:</strong> {selectedLink?.Link ? <a href={selectedLink.Link} target="_blank" rel="noreferrer">{selectedLink.Link}</a> : '-'}</p>
            <p className="truncate"><strong>Description:</strong> {safe(selectedLink?.Description)}</p>
          </div>

          <div className="text-center">
            <p className="text-base font-bold text-black">You can delete this payment link. This action cannot be undone.</p>
          </div>

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button onClick={() => { setActionModalVisible(false); setSelectedLink(null); }} disabled={deleteLoading}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleDeletePaymentLink}
              loading={deleteLoading}
              className="!bg-red-600 hover:!bg-red-700 !border-none  !text-white !font-semibold !px-6 !py-4"
            >
              {deleteLoading ? 'Deleting...' : 'Delete Payment Link'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PaymentLinks;
